import type { ReactNode } from "react";

/**
 * Builds a route where the LAYOUT is the route element and the PAGE renders
 * into the layout's <Outlet/> as the index child. Ported verbatim from
 * pollinator's core/utils/makeRoute.
 *
 * A splat path needs a second child. An index child only matches when the
 * splat remainder is empty, so `/sign-up/*` rendered the page at `/sign-up`
 * and an empty <Outlet/> — a blank screen — at `/sign-up/sso-callback`, which
 * is where Clerk lands after Google. Adding `path: "*"` gives the same page
 * for the sub-paths those components route internally.
 *
 * The test was `endsWith("/*")`, which the catch-all route `"*"` does not
 * satisfy — so every unknown URL rendered that same empty <Outlet/> and the
 * 404 page had never once been on screen. `endsWith("*")` covers both, and
 * still excludes the paths the narrower test was protecting: `/directory`
 * does not end in a star, so `/directory/junk` reaches the 404 rather than
 * rendering the directory.
 */
export const makeRoute = (
  path: string,
  element: ReactNode,
  layout: ReactNode,
  errorElement?: ReactNode
) => ({
  path,
  element: layout,
  ...(errorElement && { errorElement }),
  children: path.endsWith("*")
    ? [{ index: true, element }, { path: "*", element }]
    : [{ index: true, element }],
});

export default makeRoute;
